import React from 'react';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import CloseIcon from '@mui/icons-material/Close';
import background_image from '../../../../assets/images/onboarding/purchase_subscription_background.png';
import WarningIcon  from 'shared/assets/icons/warningiconwhite.svg';


interface EndTourModalProps {
  open: boolean;
  onClose: () => void;
  onUpgrade: () => void;
  onLearnMore?: () => void;
  buttonText: string;
  title: string;
  description: string;
  keepFreeAccountLabel?: string;
}

const EndTourModal: React.FC<EndTourModalProps> = ({open, onClose, onUpgrade, onLearnMore, buttonText, title, description, keepFreeAccountLabel}) => {
  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
          onClose();
        }
      }}
      maxWidth={false}
      aria-labelledby="subscription-modal-title"
      aria-describedby="subscription-modal-description"
      PaperProps={{
        sx: {
          borderRadius: 4,
          boxShadow: '0 8px 40px 0 rgba(44, 62, 80, 0.25)',
          maxWidth: {xs: '80vw', sm: '60vw', md: '50vw', lg: '35vw'},
          minWidth: {xs: '80vw', sm: '50vw', md: '40vw', lg: '30vw'},
          position: 'relative',
          p: {xs: 2, md: 4},
          backgroundImage: `url(${background_image})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
        },
      }}
    >
      <DialogContent
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          p: {xs: 2, sm: 3, md: 4},
          position: 'relative',
        }}
      >
        {/* Close Button */}
        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            top: {xs: 2, sm: 4, md: 6},
            right: {xs: 2, sm: 4, md: 6},
            width: 36,
            height: 36,
            color: '#E6E6E6',
            zIndex: 2,
            '&:hover': {color: '#2542CB'},
          }}
        >
          <CloseIcon fontSize="medium"/>
        </IconButton>

        {/* Shield Icon */}
        <Box mb={3}>
          <img
            src={WarningIcon}
            alt="Shield Icon"
            style={{width: '13vh', height: '13vh'}}
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = 'none';
            }}
          />
        </Box>

        {/* Title */}
        <Typography
          id="subscription-modal-title"
          variant="h4"
          sx={{
            width: {xs: '100%', sm: '95%', lg: '90%'},
            fontFamily: 'InterBold',
            fontSize: {xs: 18, md: 21, lg: 23},
            fontWeight: 600,
            color: '#fff',
            textAlign: 'center',
            mb: {xs: 1.6, sm: 2, md: 2.5},
            lineHeight: {xs: 1, md: 1.2, lg: 1.5},
          }}
        >
          {title}
        </Typography>

        {/* Description */}
        <Typography
          id="subscription-modal-description"
          sx={{
            width: {xs: '95%', sm: '85%', lg: '80%'},
            color: '#E0E7FF',
            fontWeight: 400,
            textAlign: 'center',
            mb: {xs: 2, sm: 3.5, md: 4, lg: 5},
            fontFamily: 'InterRegular',
            fontSize: {xs: 12, md: 14, lg: 16},
            lineHeight: {xs: 1.2, md: 1.2, lg: 1.5},
          }}
        >
          {description}
        </Typography>

        {/* Action Buttons */}
        <Box
          display="flex"
          gap={4}
          mb={{xs: 2, sm: 2, md: 3, lg: 4}}
          width="100%"
          justifyContent="center"
          flexWrap="wrap"
        >
          {onLearnMore && (
            <Button
              variant="contained"
              onClick={onLearnMore}
              sx={{
                background: '#8995c5',
                color: '#F3F3F3',
                fontWeight: 600,
                borderRadius: 1,
                px: 4,
                py: 1.5,
                minWidth: 170,
                boxShadow: '0 4px 6px #4F5158',
                fontSize: {xs: 13, sm: 14, md: 16},
                '&:hover': {background: '#6a7ab8'},
              }}
            >
              Learn more
            </Button>
          )}

          <Button
            variant="contained"
            onClick={onUpgrade}
            sx={{
              background: '#9035DE',
              color: '#F3F3F3',
              fontWeight: 600,
              borderRadius: 1,
              px: 4,
              py: 1.5,
              minWidth: 170,
              fontSize: {xs: 13, sm: 14, md: 16},
              '&:hover': {background: '#7C3AED'},
            }}
          >
            {buttonText}
          </Button>
        </Box>

        {keepFreeAccountLabel && (
          <Typography
            component="button"
            onClick={onClose}
          sx={{
            color: '#E0E0E0',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            fontFamily: 'InterRegular',
            fontSize: {xs: 12, md: 14, lg: 16},
            lineHeight: {xs: 1.2, md: 1.2, lg: 1.5},
            fontWeight: 500,
            p: 0,
            transition: 'color 0.2s',
            '&:hover': {color: '#fff'},
            display: 'block',
            mx: 'auto',
          }}
        >
            {keepFreeAccountLabel}
          </Typography>
        )}

      </DialogContent>
    </Dialog>
  );
};

EndTourModal.displayName = 'EndTourModal';

export default React.memo(EndTourModal);
